import { useTranslation } from 'react-i18next';

import { getProductStockStatus, isStockUnlimited } from '@r/utils/product-stock.ts';
import { cn } from '@r/utils/utils.ts';

interface ProductStockStatusProps {
  stock?: number | null;
  lowStockAlert?: number | null;
  className?: string;
}

type StockBadgeIndicator = {
  dotClassName: string;
  labelKey: string;
};

function StockStatusBadge({
  indicator,
  className
}: {
  indicator: StockBadgeIndicator;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        'inline-flex gap-2 items-center px-3 py-1 text-xs font-medium rounded-full border w-fit border-border bg-background text-foreground',
        className
      )}
    >
      <span className={cn('size-2 shrink-0 rounded-full', indicator.dotClassName)} aria-hidden />
      {t(indicator.labelKey)}
    </span>
  );
}

function getStockBadgeIndicator(
  stock: number | null | undefined,
  lowStockAlert: number | null | undefined
): StockBadgeIndicator {
  const status = getProductStockStatus(stock, lowStockAlert);

  if (status === 'out_of_stock') {
    return {
      dotClassName: 'bg-muted-foreground',
      labelKey: 'shop.product.stock.out'
    };
  }

  if (status === 'low_stock') {
    return {
      dotClassName: 'bg-orange-500',
      labelKey: 'shop.product.stock.low'
    };
  }

  if (isStockUnlimited(stock)) {
    return {
      dotClassName: 'bg-green-600',
      labelKey: 'shop.product.stock.unlimited'
    };
  }

  return {
    dotClassName: 'bg-green-600',
    labelKey: 'shop.product.stock.available'
  };
}

export function ProductStockStatus({ stock, lowStockAlert, className }: ProductStockStatusProps) {
  return (
    <StockStatusBadge
      indicator={getStockBadgeIndicator(stock, lowStockAlert)}
      className={className}
    />
  );
}
